import { HelpStep, HelpTourConfig, HelpContext } from './vacationHelp.types';
import { MIN_ADVANCE_DAYS } from '@/features/Vacations/utils/vacationStartDateRules';
import { MIN_CORRECTION_LEAD_DAYS } from '@/features/Vacations/utils/vacationStartDateRules';
import { SELL_DAYS, MIN_LONGEST_SEGMENT_DAYS, MIN_SEGMENT_DAYS, ENTITLED_DAYS_PER_PERIOD } from '@/features/Vacations/utils/vacationRules';
import { DRAFT_GRACE_MINUTES } from '@/features/Vacations/utils/vacationRequestStatus';
import { APPROVAL_LEAD_DAYS } from '@/features/Vacations/utils/vacationApprovalDeadline';
import { MONTHS_TO_REQUEST, MONTHS_TO_ENTITLEMENT } from '@/features/Vacations/utils/vacationEligibility';
import { EXPIRING_WINDOW_DAYS } from '@/features/Vacations/utils/panorama/constants';
import { SCHEDULE_LIMIT_MONTHS } from '@/features/Vacations/vacations.definitions';
import { CAPACITY_LIMIT_PERCENT } from '@/features/Vacations/pages/VacationOverviewPage/vacationOverviewPage.definitions';

export const VACATION_REQUEST_STEPS: HelpStep[] = [
    {
        element: 'vacation-balance',
        title: 'Suas informações',
        lead: 'Aqui você acompanha seus dias de férias e os prazos para agendar.',
        side: 'bottom',
    },
    {
        element: 'vacation-form-format',
        title: 'Escolha a modalidade',
        lead: 'Escolha como quer tirar seus 30 dias.',
        chips: ['30 dias', '10 + 20 dias', '15 + 15 dias'],
        bullets: [
            '30 dias: um período só',
            '10 + 20: você decide qual período vem primeiro',
            'Nas divididas, você escolhe quando começa o 2º período',
        ],
        side: 'bottom',
    },
    {
        element: 'vacation-form-start-date',
        title: 'Dias de férias',
        lead: `Escolha um dia disponível no calendário. O primeiro fica a pelo menos ${MIN_ADVANCE_DAYS} dias de hoje.`,
        bullets: [
            'Férias começam numa segunda, terça ou quarta, nunca na quinta ou sexta',
            'Férias não podem começar em feriado nem nos 2 dias antes dele',
            'Você não pode agendar para depois do seu "Limite p/ agendar"',
            'Dias que não podem ser escolhidos ficam bloqueados no calendário',
        ],
        side: 'bottom',
    },
    {
        element: 'vacation-form-selling',
        title: 'Abono pecuniário',
        lead: `Marque caso deseje manifestar interesse em converter 1/3 das férias (${SELL_DAYS} dias) em abono.`,
        bullets: [
            'O interesse é avaliado pelo Departamento Pessoal',
            'Só disponível na modalidade 30 dias corridos',
            'Marcando, suas férias passam a ter 20 dias',
        ],
        side: 'bottom',
    },
    {
        element: 'vacation-form-contacts',
        title: 'E-mails de gestores',
        lead: 'Informe quem vai aprovar suas férias.',
        bullets: [
            'Gestor Raro: um e-mail @rarolabs.com.br',
            'Cliente, se houver: até 5 e-mails. Digite e aperte Enter para adicionar',
            'Basta um dos clientes aprovar',
        ],
        side: 'bottom',
    },
    {
        element: 'vacation-form-submit',
        title: 'Confirmar e enviar',
        lead: `Clique em "Enviar solicitação".`,
        bullets: [
            `Fica "Aguardando envio ao gestor" por ${DRAFT_GRACE_MINUTES} minutos`,
            'Você pode "Excluir solicitação" nesse tempo',
            'Depois segue automaticamente para o gestor e cliente',
        ],
        side: 'bottom',
    },
    {
        element: 'vacation-requests-list',
        title: 'Acompanhe seu pedido',
        description: 'Veja o status, as decisões do gestor, do cliente e do Departamento Pessoal, e a linha do tempo.',
        side: 'top',
    },
];

export const VACATION_APPROVAL_STEPS: HelpStep[] = [
    {
        element: 'approval-filters',
        title: 'Filtros e pesquisa',
        lead: 'Filtre por tipo (Férias ou Day-off) e busque pedidos que precisam da sua aprovação.',
        tip: 'Você vê apenas os pedidos direcionados a você.',
        side: 'bottom',
    },
    {
        element: 'approval-carousel',
        title: 'Cartão do pedido',
        lead: 'Veja as datas, o status do cliente (férias) e se há conflito de alocação. Day-offs mostram a data da folga e quando expiram.',
        bullets: [
            { label: 'Âmbar', text: '15 dias ou menos para decidir', tone: 'amber' },
            { label: 'Vermelho', text: '7 dias ou menos', tone: 'red' },
        ],
        side: 'bottom',
    },
    {
        element: 'approval-demo-card',
        title: 'Exemplo de pedido',
        lead: 'Este é um pedido de exemplo. Os reais funcionam do mesmo jeito.',
        bullets: [
            'No topo: quem pediu, o status e a resposta do cliente',
            'No meio: a modalidade, os períodos e o abono, se houver',
            'Aviso quando as férias coincidem com uma alocação',
            'Embaixo: Aprovar ou Reprovar. Ao reprovar, o motivo é obrigatório',
        ],
        side: 'right',
    },
    {
        element: 'approval-decided-list',
        title: 'Já respondidas por você',
        description: 'Pedidos que você aprovou, reprovou ou que expiraram.',
        side: 'top',
    },
];

export const VACATION_MANAGEMENT_COLLABORATORS_STEPS: HelpStep[] = [
    {
        element: 'dp-search',
        title: 'Busca de colaboradores',
        description: 'Busque por nome ou e-mail.',
        side: 'bottom',
    },
    {
        element: 'dp-filters',
        title: 'Clientes',
        lead: 'Filtre a tabela pelo cliente.',
        bullets: [
            'Cliente: mostra quem está alocado nele. O número entre parênteses é a quantidade de pessoas',
            'O painel mostra só colaboradores CLT ativos',
        ],
        side: 'bottom',
    },
    {
        element: 'dp-status-filters',
        title: 'Filtros disponíveis',
        lead: 'Clique em um cartão para filtrar. Clique de novo para remover.',
        bullets: [
            'Mostra só os colaboradores daquela situação',
            'Um filtro por vez',
            'Em Férias, Agendadas e Férias concluídas mostram o filtro de mês e ano',
        ],
        side: 'bottom',
    },
    {
        element: 'dp-table',
        title: 'Painel do Departamento Pessoal',
        lead: 'Cada linha é um colaborador, com saldo, prazos e o pedido em aberto.',
        bullets: [
            'No botão de colunas, escolha quais ver e em que ordem. Sua escolha fica salva',
            'Clique no título de uma coluna para ordenar',
            'Olho (Visualizar solicitação): abre o pedido para decidir',
            'Relógio (Ver histórico): mostra as férias anteriores do colaborador',
        ],
        side: 'top',
    },
    {
        element: 'dp-decision-demo',
        demo: 'dp-decision',
        title: 'Decidir um pedido',
        lead: 'Exemplo de pedido aguardando o Departamento Pessoal.',
        bullets: [
            'Valide ou reprove cada período separadamente',
            'Ao reprovar, o motivo é obrigatório (mínimo 10 caracteres)',
            'Reprovando só um período, o colaborador escolhe novas datas',
            'Conflitos com alocação e com a equipe aparecem antes da decisão',
            'O histórico mostra cada etapa do pedido',
        ],
        side: 'left',
    },
];

export const VACATION_MANAGEMENT_CONFLICTS_STEPS: HelpStep[] = [
    {
        element: 'conflicts-filters',
        demo: 'conflicts',
        title: 'Período e filtros',
        lead: 'Escolha o ano e o mês que quer analisar.',
        bullets: [
            'Entram pedidos aguardando aprovação, agendados e em férias',
            'Aparece quem tem férias que tocam o período, mesmo que comecem antes',
            'Todos os meses: considera o ano inteiro',
            '"Mostrar só conflitos de cobertura" deixa só os pedidos aguardando que batem com outro pedido aguardando',
        ],
        side: 'bottom',
    },
    {
        element: 'conflicts-summary',
        demo: 'conflicts',
        title: 'Resumo',
        lead: 'Quantos clientes, pessoas e solicitações com conflito há no período.',
        side: 'bottom',
    },
    {
        element: 'conflicts-client-header',
        demo: 'conflicts',
        title: 'Cliente',
        lead: 'Cada bloco é um cliente. Clique para abrir.',
        bullets: [
            { label: 'Pessoas', text: 'quem tem férias no período', tone: 'primary' },
            { label: 'Aguardando aprovação', text: 'pedidos ainda sem decisão', tone: 'primary' },
            { label: 'Com conflito', text: 'pedidos aguardando que batem com outro aguardando', tone: 'amber' },
        ],
        side: 'bottom',
    },
    {
        element: 'conflicts-responsible-header',
        demo: 'conflicts',
        title: 'Responsável',
        lead: 'Dentro do cliente, as pessoas são separadas pelo responsável da alocação.',
        bullets: [
            'O conflito só é calculado entre pessoas do mesmo cliente e do mesmo responsável',
            '"Sem responsável definido" reúne quem não tem responsável na alocação',
        ],
        side: 'bottom',
    },
    {
        element: 'conflicts-period-card',
        demo: 'conflicts',
        title: 'Pedido',
        lead: 'Cada cartão é um pedido, separado em "Aguardando aprovação" e "Agendadas e em férias".',
        bullets: [
            'Nome, situação, períodos e data da solicitação',
            { label: 'Borda âmbar', text: 'conflito com outro pedido aguardando', tone: 'amber' },
            { label: 'Borda azul', text: 'só colegas já agendados nas datas', tone: 'blue' },
            'Borda cinza: sem conflito',
        ],
        side: 'top',
    },
    {
        element: 'conflicts-badges',
        demo: 'conflicts',
        title: 'Tipos de conflito',
        lead: 'Os selos dizem com quem o pedido bate.',
        bullets: [
            { label: 'Sobrepõe com', text: 'dias em comum com outro pedido aguardando', tone: 'amber' },
            { label: 'Próximo de', text: 'sem dias em comum, mas a até 7 dias de distância', tone: 'sky' },
            { label: 'Já agendado no período', text: 'colega já aprovado nas mesmas datas', tone: 'blue' },
        ],
        tip: 'Pedidos já aprovados não mostram conflito com pedidos que ainda aguardam. O aviso aparece no pedido que está para ser decidido.',
        side: 'top',
    },
    {
        element: 'conflicts-people-panel',
        demo: 'conflicts',
        title: 'Dias em conflito',
        lead: '"Ver detalhes" lista cada pessoa que bate com o pedido.',
        bullets: [
            'O selo mostra quantos dias se sobrepõem, ou a distância em dias',
            'Na barra, o cinza é o período do pedido e a parte colorida são os dias em comum',
        ],
        side: 'right',
    },
    {
        element: 'conflicts-calendar',
        demo: 'conflicts',
        title: 'Calendário',
        lead: 'Compare as férias de todos na mesma régua de meses e dias.',
        bullets: [
            { label: 'Roxo', text: 'a pessoa do pedido, sempre na 1ª linha', tone: 'primary' },
            { label: 'Âmbar', text: 'pedido aguardando com dias em comum', tone: 'amber' },
            { label: 'Azul claro', text: 'pedido aguardando a até 7 dias', tone: 'sky' },
            { label: 'Listrado azul', text: 'já agendado', tone: 'blue' },
            { label: 'Listrado verde', text: 'em férias agora', tone: 'green' },
        ],
        tip: 'Passe o mouse em uma barra para ver as datas.',
        side: 'top',
    },
    {
        element: 'conflicts-view-request',
        demo: 'conflicts',
        title: 'Ver solicitação',
        lead: 'Nos pedidos aguardando, abre o pedido para validar ou reprovar.',
        bullets: ['Clique no nome da pessoa para abrir o perfil'],
        side: 'left',
    },
];

export const VACATION_MANAGEMENT_HISTORY_STEPS: HelpStep[] = [
    {
        element: 'history-situation',
        demo: 'history',
        title: 'Situação',
        lead: 'Escolha qual momento do pedido você quer ver.',
        bullets: [
            'Pedido feito: quando o colaborador enviou',
            'Aprovada pelo gestor e Validada pelo Departamento Pessoal: quando cada um aprovou',
            'Férias iniciadas: quando cada parcela começou',
            'Férias concluídas: quando cada parcela terminou',
            'Recusada: quando houve recusa ou o prazo venceu',
        ],
        side: 'bottom',
    },
    {
        element: 'history-period',
        demo: 'history',
        title: 'Ano e mês',
        lead: 'O período filtra pela data da situação escolhida.',
        bullets: [
            'Todos os meses: considera o ano inteiro',
            'Ao abrir a aba, vem o mês atual',
            'Meses futuros mostram férias já aprovadas que vão começar ou terminar ali',
        ],
        tip: 'Exemplo: Férias iniciadas em novembro mostra só quem começou em novembro. Quem começou em outubro e termina em novembro aparece em Férias concluídas.',
        side: 'bottom',
    },
    {
        element: 'history-search',
        demo: 'history',
        title: 'Busca por colaborador',
        lead: 'Busque por nome ou e-mail.',
        bullets: ['Não diferencia acentos', 'Funciona junto com a situação e o período'],
        side: 'bottom',
    },
    {
        element: 'history-summary',
        demo: 'history',
        title: 'Resumo e exportação',
        lead: 'A frase resume o filtro: situação, período, solicitações e pessoas.',
        bullets: [
            'Baixar CSV exporta somente o que está na tela, com uma linha por parcela',
            'O arquivo traz colaborador, datas, dias, abono, situação e quem aprovou',
        ],
        side: 'bottom',
    },
    {
        element: 'history-card',
        demo: 'history',
        title: 'Cartão da solicitação',
        lead: 'Cada cartão é uma solicitação. Clique nele para ver o histórico completo daquela solicitação.',
        bullets: [
            'Nome, e-mail e situação atual',
            'Em Férias iniciadas e concluídas, só aparecem as parcelas do período filtrado',
            'Abono, quando houver, e o período aquisitivo de onde saíram os dias',
        ],
        side: 'top',
    },
    {
        element: 'history-timeline',
        demo: 'history',
        title: 'Linha do tempo',
        lead: 'Acompanhe cada etapa do pedido, com as datas.',
        bullets: [
            'Abra e feche em "Linha do Tempo", no próprio cartão',
            'Verde: já aconteceu. Cinza: previsto. Vermelho: recusada',
            'A etapa da situação filtrada fica destacada',
        ],
        side: 'top',
    },
    {
        element: 'history-manual-card',
        demo: 'history',
        title: 'Lançamento manual',
        lead: 'Férias registradas no cadastro do colaborador, sem pedido no sistema.',
        bullets: [
            'Só aparecem em Férias iniciadas e Férias concluídas',
            'Não abrem detalhes nem têm linha do tempo',
        ],
        side: 'top',
    },
];

export const VACATION_MANAGEMENT_PAIDREST_STEPS: HelpStep[] = [
    {
        element: 'paidrest-search',
        title: 'Busca de PJ',
        lead: 'Busque por nome, CNPJ ou empresa.',
        bullets: [
            'O resultado aparece enquanto você digita',
            'Os cartões ao lado passam a contar só o resultado da busca',
        ],
        side: 'bottom',
    },
    {
        element: 'paidrest-table',
        title: 'Tabela e histórico',
        lead: 'Veja o contrato e o último descanso de cada PJ.',
        bullets: [
            'Direito: se o contrato prevê descanso remunerado',
            'Clique no nome para abrir o perfil',
            '"Ver histórico" mostra todos os descansos',
        ],
        side: 'bottom',
    },
    {
        element: 'paidrest-history-demo',
        demo: 'paidrest-history',
        title: 'Histórico de descanso',
        lead: 'Exemplo do histórico de um PJ.',
        bullets: [
            'Descansos agrupados por ano, com a quantidade de dias',
            'No topo, o total do ano atual e o total geral',
        ],
        side: 'left',
    },
    {
        element: 'paidrest-filters',
        title: 'Situação e direito',
        lead: 'Filtre os PJs pelos cartões ao lado.',
        bullets: [
            'Situação: em descanso hoje ou fora de descanso',
            'Direito: com ou sem descanso remunerado no contrato',
            'Marcar os dois cartões de um grupo é o mesmo que não filtrar',
        ],
        side: 'left',
    },
];

export const VACATION_MANAGEMENT_HOLIDAYS_STEPS: HelpStep[] = [
    {
        element: 'holidays-section',
        title: 'Feriados',
        lead: `Bloqueiam o início das férias no dia e 2 dias antes.`,
        tip: 'Você pode duplicar feriados do ano anterior.',
        side: 'bottom',
    },
    {
        element: 'holidays-add',
        title: 'Adicionar feriado',
        description: 'Clique para abrir o formulário.',
        side: 'bottom',
    },
];

export const VACATION_OVERVIEW_DASHBOARD_STEPS: HelpStep[] = [
    {
        element: 'overview-kpi-today',
        title: 'Em férias hoje',
        lead: 'Percentual da base descansando agora.',
        description: 'Clique para filtrar a tabela.',
        side: 'bottom',
    },
    {
        element: 'overview-kpi-next-month',
        title: 'Férias no próximo mês',
        description: 'Quantas pessoas têm férias agendadas ou já estão gozando.',
        side: 'bottom',
    },
    {
        element: 'overview-kpi-pending',
        title: 'Aguardando aprovação',
        lead: `Pedidos que precisam decisão (prazo: ${APPROVAL_LEAD_DAYS} dias antes do início).`,
        tip: 'Urgentes em vermelho.',
        side: 'bottom',
    },
    {
        element: 'overview-kpi-risk',
        title: 'Risco: pagamento em dobro (art. 137)',
        lead: 'Períodos vencidos e próximos de vencer (até 90 dias).',
        tip: 'Férias não gozadas no prazo geram dobra na rescisão.',
        side: 'bottom',
    },
    {
        element: 'overview-capacity',
        title: 'Capacidade (por semana)',
        lead: `Até ${CAPACITY_LIMIT_PERCENT}% da equipe pode sair por semana.`,
        description: 'Verde = abaixo do limite | Amarelo/Vermelho = acima',
        side: 'bottom',
    },
];

export const VACATION_OVERVIEW_TABLE_STEPS: HelpStep[] = [
    {
        element: 'overview-table-filters',
        title: 'Filtrar a tabela',
        description: 'Use status, período, cliente e pessoa para ver só o que interessa.',
        side: 'bottom',
    },
    {
        element: 'overview-table-status',
        title: 'Por situação',
        description: 'Clique para mostrar/ocultar cada status.',
        side: 'bottom',
    },
    {
        element: 'overview-table',
        title: 'Pessoas em férias',
        description: 'Lista com datas, status e quem decide cada etapa.',
        side: 'top',
    },
];
